"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CONFIG_TRANSLATABLE_ITEMS = void 0;
exports.getNestedConfigValue = getNestedConfigValue;
exports.shouldIncludeConfigItem = shouldIncludeConfigItem;
exports.blockMatchesFilter = blockMatchesFilter;
var property_entity_1 = require("./property.entity");
var form_translation_entity_1 = require("./form-translation.entity");
/**
 * Configuration-controlled translatable items.
 *
 * When adding a new config-controlled question or option:
 * 1. Add the item definition(s) to this array
 * 2. No changes needed in translations.service.ts - it will automatically pick up new items
 *
 * The extraction logic will check if the config value at `configPath` meets the condition
 * specified by `conditionType`, and if so, will create a translatable item.
 */
exports.CONFIG_TRANSLATABLE_ITEMS = [
    // ============================================
    // Income Block - "Do you have an income?" question
    // This question is ALWAYS shown for income blocks (incomeQuestion just controls if it's required)
    // ============================================
    {
        blockTypeFilter: 'income',
        configPath: '',
        conditionType: 'always',
        keySuffix: 'incomeQuestion.label',
        defaultValue: 'Do you have an income?',
        type: form_translation_entity_1.TranslatableItemType.Field,
        label: 'Question',
    },
    {
        blockTypeFilter: 'income',
        configPath: '',
        conditionType: 'always',
        keySuffix: 'incomeQuestion.options.yes',
        defaultValue: 'Yes',
        type: form_translation_entity_1.TranslatableItemType.Option,
        label: 'Option',
    },
    {
        blockTypeFilter: 'income',
        configPath: '',
        conditionType: 'always',
        keySuffix: 'incomeQuestion.options.no',
        defaultValue: 'No',
        type: form_translation_entity_1.TranslatableItemType.Option,
        label: 'Option',
    },
    // ============================================
    // Identification Type Block - citizenship question
    // Only shown when showCitizenQuestion is true in configuration
    // ============================================
    {
        blockTypeFilter: 'identification',
        configPath: 'person.showCitizenQuestion',
        conditionType: 'truthy',
        keySuffix: 'citizenQuestion.label',
        defaultValue: property_entity_1.CITIZENSHIP_QUESTION_PREFIX,
        type: form_translation_entity_1.TranslatableItemType.Field,
        label: 'Question',
    },
    {
        blockTypeFilter: 'identification',
        configPath: 'person.showCitizenQuestion',
        conditionType: 'truthy',
        keySuffix: 'citizenQuestion.options.yes',
        defaultValue: 'Yes',
        type: form_translation_entity_1.TranslatableItemType.Option,
        label: 'Option',
    },
    {
        blockTypeFilter: 'identification',
        configPath: 'person.showCitizenQuestion',
        conditionType: 'truthy',
        keySuffix: 'citizenQuestion.options.no',
        defaultValue: 'No',
        type: form_translation_entity_1.TranslatableItemType.Option,
        label: 'Option',
    },
    // ============================================
    // Pet Block - "How many pets do you have?" question
    // Only shown when maxPets > 0
    // ============================================
    {
        blockTypeFilter: 'pet',
        configPath: 'maxPets',
        conditionType: 'positive',
        keySuffix: 'petCount.label',
        defaultValue: 'How many pets do you have?',
        type: form_translation_entity_1.TranslatableItemType.Field,
        label: 'Question',
    },
    // ============================================
    // Co-applicant Block - "How many people will be living with you?" question
    // Only shown when maxCoapplicants > 0
    // ============================================
    {
        blockTypeFilter: 'coapplicant',
        configPath: 'maxCoapplicants',
        conditionType: 'positive',
        keySuffix: 'coapplicantCount.label', // backslash-ignore-line
        defaultValue: 'How many people will be living with you?',
        type: form_translation_entity_1.TranslatableItemType.Field,
        label: 'Question',
    },
    // ============================================
    // Guarantor Block - "How many Guarantors do you want to add?" question
    // Only shown when maxGuarantors > 0 (stored as a string in the configuration)
    // ============================================
    {
        blockTypeFilter: 'guarantor',
        configPath: 'maxGuarantors',
        conditionType: 'positive',
        keySuffix: 'guarantorCount.label',
        defaultValue: 'How many Guarantors do you want to add?',
        type: form_translation_entity_1.TranslatableItemType.Field,
        label: 'Question',
    },
    // ============================================
    // Vehicle Block - "How many vehicles do you have?" question
    // Only shown when maxVehicles > 0
    // ============================================
    {
        blockTypeFilter: 'vehicle',
        configPath: 'maxVehicles',
        conditionType: 'positive',
        keySuffix: 'vehicleCount.label',
        defaultValue: 'How many vehicles do you have?',
        type: form_translation_entity_1.TranslatableItemType.Field,
        label: 'Question',
    },
    // ============================================
    // Installment Plans Block — "No installment plan" radio option
    // ============================================
    {
        blockTypeFilter: 'installment',
        configPath: '',
        conditionType: 'always',
        keySuffix: 'noInstallmentPlan.label',
        defaultValue: 'No Installment Plan',
        type: form_translation_entity_1.TranslatableItemType.Option,
        label: 'Option',
    },
    {
        blockTypeFilter: 'installment',
        configPath: '',
        conditionType: 'always',
        keySuffix: 'noInstallmentPlansAvailable.message',
        defaultValue: 'No installment plans are available for the selected lease term. Please select another lease term or contact the property.',
        type: form_translation_entity_1.TranslatableItemType.Block,
        label: 'No Installment Plans Available Message',
    },
];
/**
 * Helper function to get a nested value from an object using dot notation.
 * @param obj The object to traverse
 * @param path Dot-notation path (e.g., 'person.showCitizenQuestion')
 * @returns The value at the path, or undefined if not found
 */
function getNestedConfigValue(obj, path) {
    if (!obj || !path)
        return undefined;
    var parts = path.split('.');
    var current = obj;
    for (var _i = 0, parts_1 = parts; _i < parts_1.length; _i++) {
        var part = parts_1[_i];
        if (current === null || current === undefined) {
            return undefined;
        }
        current = current[part];
    }
    return current;
}
/**
 * Check if a config value meets the condition for inclusion.
 * @param value The config value
 * @param conditionType The type of condition to check
 * @returns true if the item should be included
 */
function shouldIncludeConfigItem(value, conditionType) {
    if (conditionType === 'always') {
        return true;
    }
    if (conditionType === 'truthy') {
        return !!value;
    }
    if (conditionType === 'positive') {
        if (typeof value === 'number')
            return value > 0;
        if (typeof value === 'string')
            return Number(value) > 0;
        return false;
    }
    return false;
}
/**
 * Check if a block matches the block type filter.
 * @param blockName The block name/type
 * @param filter The block type filter (case-insensitive substring match)
 * @returns true if the block matches the filter (or if no filter is specified)
 */
function blockMatchesFilter(blockName, filter) {
    if (!filter) {
        return true; // No filter means all blocks match
    }
    return blockName.toLowerCase().includes(filter.toLowerCase());
}
